import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowSquareOut, Check } from "@phosphor-icons/react";
import Navbar from "@/components/site/Navbar";
import Footer from "@/components/site/Footer";
import SubpageHero from "@/components/site/SubpageHero";
import FAQ from "@/components/site/FAQ";
import Contact from "@/components/site/Contact";
import NotFound from "@/pages/NotFound";
import useSeo from "@/hooks/useSeo";
import { INDUSTRIES } from "@/lib/industries";
import { faqJsonLd } from "@/lib/faqs";

const includes = [
  "Mobile-optimized website",
  "Domain setup, hosting & SSL — handled",
  "Click-to-call & tap-to-text buttons",
  "Quote & contact forms",
  "Instant lead alerts to your email",
  "Private client portal to update your own info",
  "Security patches, backups & uptime monitoring",
];

export default function IndustryPage() {
  const { slug } = useParams();
  const data = INDUSTRIES[slug];
  const path = `/websites-for/${slug}`;

  useSeo(
    data
      ? {
          title: data.seoTitle,
          description: data.description,
          path,
          jsonLd: {
            "@context": "https://schema.org",
            "@graph": [
              {
                "@type": "BreadcrumbList",
                itemListElement: [
                  { "@type": "ListItem", position: 1, name: "WebJesters", item: "https://webjesters.com/" },
                  { "@type": "ListItem", position: 2, name: `Websites for ${data.name}`, item: `https://webjesters.com${path}` },
                ],
              },
              {
                "@type": "Service",
                name: `${data.name} website design and management`,
                serviceType: "Website design, hosting and management",
                provider: { "@id": "https://webjesters.com/#organization" },
                areaServed: { "@type": "Country", name: "United States" },
                url: `https://webjesters.com${path}`,
                description: data.description,
                offers: [
                  { "@type": "Offer", name: "Website build", price: "499", priceCurrency: "USD" },
                  {
                    "@type": "Offer",
                    name: "Hosting & management",
                    price: "79",
                    priceCurrency: "USD",
                    priceSpecification: { "@type": "UnitPriceSpecification", price: "79", priceCurrency: "USD", unitText: "MONTH" },
                  },
                ],
              },
              faqJsonLd(data.faqs),
            ],
          },
        }
      : { title: "Page not found — WebJesters", noindex: true }
  );

  if (!data) return <NotFound />;
  const ex = data.example;

  return (
    <main id="main-content" data-testid={`industry-page-${slug}`} className="relative">
      <Navbar />
      <SubpageHero
        crumb={`Websites for ${data.name}`}
        eyebrow={`Websites for ${data.name}`}
        title={data.title}
        intro={data.intro}
      >
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <a
            href="#contact"
            data-testid="industry-cta"
            className="group inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full bg-white text-black font-medium hover:bg-zinc-200 transition-colors"
          >
            Request a build
            <ArrowUpRight size={18} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </a>
          <a
            href="#example"
            className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-full border border-white/15 bg-white/[0.03] text-white font-medium hover:bg-white/[0.07] transition-colors"
          >
            See the live example
          </a>
        </div>
      </SubpageHero>

      {/* What the site needs */}
      <section className="relative py-14 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14">
          <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-400 flex items-center gap-3">
            <span className="block w-6 h-px bg-zinc-600" />
            What {data.name.toLowerCase()} sites have to do
          </div>
          <div className="mt-10 grid sm:grid-cols-2 gap-x-10 gap-y-9">
            {data.needs.map(([h, p], i) => (
              <motion.div
                key={h}
                initial={{ opacity: 0, y: 16 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
              >
                <h2 className="font-display text-xl sm:text-[22px] tracking-tight text-white">{h}</h2>
                <p className="mt-3 text-[14.5px] text-zinc-400 leading-relaxed max-w-md">{p}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Live example */}
      <section id="example" className="relative py-14 sm:py-24 bg-[#0c0c0d] border-y border-white/[0.06] scroll-mt-20">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6">
            <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-400 flex items-center gap-3">
              <span className="block w-6 h-px bg-zinc-600" />
              {ex.label}
            </div>
            <h2 className="mt-5 font-display text-3xl sm:text-4xl lg:text-[44px] leading-[1.05] tracking-tight text-white">
              {ex.name}
            </h2>
            <div className="mt-2 text-[13px] text-zinc-400">{ex.category}</div>
            <p className="mt-6 text-[15px] text-zinc-400 leading-relaxed max-w-lg">{ex.summary}</p>
            <a
              href={ex.url}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="industry-example-link"
              className="mt-7 inline-flex items-center gap-2 px-6 py-3 rounded-full border border-white/[0.12] text-white text-[13.5px] hover:bg-white/[0.04] hover:border-white/[0.22] transition-colors"
            >
              Open {ex.domain}
              <ArrowSquareOut size={13} weight="bold" />
            </a>
          </div>
          <div className="lg:col-span-6 flex justify-center">
            <div className="phone-mockup w-[240px] sm:w-[280px]">
              <div className="notch" />
              <div className="screen bg-black">
                <img
                  src={ex.mobile}
                  alt={`${ex.name} — mobile website built by WebJesters`}
                  width="780"
                  height="1356"
                  loading="lazy"
                  className="w-full h-full object-cover object-top"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing summary */}
      <section className="relative py-14 sm:py-24">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 lg:px-14 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="text-[11px] tracking-[0.22em] uppercase text-zinc-400 flex items-center gap-3">
              <span className="block w-6 h-px bg-zinc-600" />
              Pricing · Website
            </div>
            <div className="mt-6 flex items-baseline flex-wrap gap-x-3 gap-y-1">
              <span className="inline-flex items-baseline gap-x-2 whitespace-nowrap">
                <span className="font-display text-5xl sm:text-6xl tracking-tighter text-white leading-none">$499</span>
                <span className="text-zinc-400">build</span>
              </span>
              <span className="text-zinc-700 text-3xl mx-1">+</span>
              <span className="inline-flex items-baseline gap-x-2 whitespace-nowrap">
                <span className="font-display text-4xl sm:text-5xl tracking-tighter text-white leading-none">$79</span>
                <span className="text-zinc-400">/month</span>
              </span>
            </div>
            <p className="mt-5 text-[14.5px] text-zinc-400 leading-relaxed">
              $99 deposit to start. Remaining balance due before launch. Monthly
              management begins once your site goes live. Month-to-month. Cancel anytime.
            </p>
            <p className="mt-4 text-[13px] text-zinc-400">
              Want your Google listing and socials handled too?{" "}
              <Link to="/local-visibility" className="text-zinc-300 underline underline-offset-4 hover:text-white">
                See Local Visibility
              </Link>{" "}
              — a separate service, $149 setup + $299/month.
            </p>
          </div>
          <ul className="lg:col-span-7 grid sm:grid-cols-2 gap-x-8 gap-y-3.5 lg:pt-2">
            {includes.map((line) => (
              <li key={line} className="flex items-start gap-3">
                <Check size={14} weight="bold" className="mt-1 flex-shrink-0 text-zinc-400" />
                <span className="text-[14.5px] text-zinc-300 leading-relaxed">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <FAQ items={data.faqs} title={`${data.name} website questions.`} />
      <Contact />
      <Footer />
    </main>
  );
}
